/**
 * PDP Delivery Estimate.
 *
 * Drives the three cooperating parts on the product page: the postcode input
 * state, the persistent bar that replaces it after a successful check, and the
 * responsive delivery drawer (`shipping-drawer`).
 *
 * All runtime configuration — thresholds, base costs, translated strings and
 * the variant → price map — is read from the JSON config block emitted by
 * `shipping-estimate.liquid`, so no product JSON is ever refetched. Live
 * shipping rates are fetched once per `postcode|country`: cached in
 * sessionStorage for the session, and saved as a per-country snapshot in
 * localStorage so a returning shopper's rates paint instantly next session.
 * Cart and variant changes re-render locally from whichever cache is live. A
 * silent page-load restore renders the threshold bar immediately but defers
 * the rate fetch itself until the drawer is first opened.
 */

/* ---------------------------------------------------------------------------
 * Locality tables (postcode → state/province)
 * ------------------------------------------------------------------------- */

const AU_POSTCODE_RANGES = [
  { min: 200, max: 299, code: 'ACT' }, { min: 800, max: 999, code: 'NT' },
  { min: 1000, max: 2599, code: 'NSW' }, { min: 2600, max: 2618, code: 'ACT' },
  { min: 2619, max: 2899, code: 'NSW' }, { min: 2900, max: 2920, code: 'ACT' },
  { min: 2921, max: 2999, code: 'NSW' }, { min: 3000, max: 3999, code: 'VIC' },
  { min: 4000, max: 4999, code: 'QLD' }, { min: 5000, max: 5799, code: 'SA' },
  { min: 6000, max: 6797, code: 'WA' }, { min: 6800, max: 6999, code: 'WA' },
  { min: 7000, max: 7999, code: 'TAS' },
];

/** US ZIP3 (first three digits) → state/territory, incl. military codes AA/AE/AP and VI/PR/GU territories. */
const US_ZIP3_RANGES = [
  { min: 5, max: 5, code: 'NY' }, { min: 6, max: 7, code: 'PR' }, { min: 8, max: 8, code: 'VI' },
  { min: 9, max: 9, code: 'PR' }, { min: 10, max: 27, code: 'MA' }, { min: 28, max: 29, code: 'RI' },
  { min: 30, max: 38, code: 'NH' }, { min: 39, max: 49, code: 'ME' }, { min: 50, max: 59, code: 'VT' },
  { min: 60, max: 69, code: 'CT' }, { min: 70, max: 89, code: 'NJ' }, { min: 90, max: 98, code: 'AE' },
  { min: 100, max: 149, code: 'NY' }, { min: 150, max: 196, code: 'PA' }, { min: 197, max: 199, code: 'DE' },
  { min: 200, max: 200, code: 'DC' }, { min: 201, max: 201, code: 'VA' }, { min: 202, max: 205, code: 'DC' },
  { min: 206, max: 219, code: 'MD' }, { min: 220, max: 246, code: 'VA' }, { min: 247, max: 268, code: 'WV' },
  { min: 270, max: 289, code: 'NC' }, { min: 290, max: 299, code: 'SC' }, { min: 300, max: 319, code: 'GA' },
  { min: 320, max: 339, code: 'FL' }, { min: 340, max: 340, code: 'AA' }, { min: 341, max: 349, code: 'FL' },
  { min: 350, max: 369, code: 'AL' }, { min: 370, max: 385, code: 'TN' }, { min: 386, max: 397, code: 'MS' },
  { min: 398, max: 399, code: 'GA' }, { min: 400, max: 427, code: 'KY' }, { min: 430, max: 459, code: 'OH' },
  { min: 460, max: 479, code: 'IN' }, { min: 480, max: 499, code: 'MI' }, { min: 500, max: 528, code: 'IA' },
  { min: 530, max: 549, code: 'WI' }, { min: 550, max: 567, code: 'MN' }, { min: 570, max: 577, code: 'SD' },
  { min: 580, max: 588, code: 'ND' }, { min: 590, max: 599, code: 'MT' }, { min: 600, max: 629, code: 'IL' },
  { min: 630, max: 658, code: 'MO' }, { min: 660, max: 679, code: 'KS' }, { min: 680, max: 693, code: 'NE' },
  { min: 700, max: 714, code: 'LA' }, { min: 716, max: 729, code: 'AR' }, { min: 730, max: 732, code: 'OK' },
  { min: 733, max: 733, code: 'TX' }, { min: 734, max: 749, code: 'OK' }, { min: 750, max: 799, code: 'TX' },
  { min: 800, max: 816, code: 'CO' }, { min: 820, max: 831, code: 'WY' }, { min: 832, max: 838, code: 'ID' },
  { min: 840, max: 847, code: 'UT' }, { min: 850, max: 865, code: 'AZ' }, { min: 870, max: 884, code: 'NM' },
  { min: 885, max: 885, code: 'TX' }, { min: 889, max: 898, code: 'NV' }, { min: 900, max: 961, code: 'CA' },
  { min: 962, max: 966, code: 'AP' }, { min: 967, max: 968, code: 'HI' }, { min: 969, max: 969, code: 'GU' },
  { min: 970, max: 979, code: 'OR' }, { min: 980, max: 994, code: 'WA' }, { min: 995, max: 999, code: 'AK' },
];

const CA_POSTAL_PREFIXES = {
  A: 'NL', B: 'NS', C: 'PE', E: 'NB', G: 'QC', H: 'QC', J: 'QC',
  K: 'ON', L: 'ON', M: 'ON', N: 'ON', P: 'ON', R: 'MB', S: 'SK',
  T: 'AB', V: 'BC', X: 'NT', Y: 'YT',
};

/** Per-market postcode format patterns for client-side pre-validation. */
const POSTCODE_PATTERNS = {
  AU: /^\d{4}$/,
  US: /^\d{5}$/,
  CA: /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/,
  GB: /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/,
};

const LEGACY_STORAGE_KEY = 'peppermayo_delivery_postcode';
const RATES_CACHE_PREFIX = 'peppermayo_delivery_rates:';
const RATES_CACHE_TTL_MS = 15 * 60 * 1000;
const RATES_SNAPSHOT_PREFIX = 'peppermayo_delivery_rates_snapshot:';
const SNAPSHOT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const POLL_MAX_MS = 10000;

/**
 * Derive a state/province code from a postcode for the given market.
 * @param {string} postcode - The postcode/ZIP entered by the shopper.
 * @param {string} countryCode - ISO country code (e.g. 'AU').
 * @returns {string} The province code, or '' if it cannot be derived.
 */
function getProvinceFromPostcode(postcode, countryCode) {
  if (countryCode === 'AU') {
    const num = parseInt(postcode, 10);
    if (Number.isNaN(num)) return '';
    const match = AU_POSTCODE_RANGES.find((r) => num >= r.min && num <= r.max);
    return match ? match.code : '';
  }
  if (countryCode === 'US') {
    const prefix = parseInt(postcode.substring(0, 3), 10);
    if (Number.isNaN(prefix)) return '';
    const match = US_ZIP3_RANGES.find((r) => prefix >= r.min && prefix <= r.max);
    return match ? match.code : '';
  }
  if (countryCode === 'CA') {
    return CA_POSTAL_PREFIXES[postcode.charAt(0).toUpperCase()] || '';
  }
  return '';
}

/**
 * Validate a postcode against its market's format before hitting the network.
 * Unknown markets accept any non-empty value (server-side validation applies).
 * @param {string} postcode - Trimmed postcode.
 * @param {string} countryCode - ISO country code.
 * @returns {boolean} Whether the postcode looks valid for the market.
 */
function isValidPostcode(postcode, countryCode) {
  const pattern = POSTCODE_PATTERNS[countryCode];
  if (!pattern) return postcode.length > 0;
  return pattern.test(postcode);
}

/**
 * Normalise raw postcode input for validation, storage, and display: trims,
 * collapses internal whitespace runs to a single space, then applies the
 * market's canonical spacing/casing — AU/US postcodes carry no letters so
 * spaces are stripped entirely, while CA/GB postcodes are conventionally
 * written uppercase.
 * @param {string} raw - The raw input value.
 * @param {string} countryCode - ISO country code (e.g. 'AU').
 * @returns {string} The normalised postcode.
 */
function normalisePostcode(raw, countryCode) {
  let postcode = (raw || '').trim().replace(/\s+/g, ' ');
  if (countryCode === 'AU' || countryCode === 'US') {
    postcode = postcode.replace(/\s/g, '');
  }
  if (countryCode === 'CA' || countryCode === 'GB') {
    postcode = postcode.toUpperCase();
  }
  return postcode;
}

/* ---------------------------------------------------------------------------
 * Money & date formatting
 * ------------------------------------------------------------------------- */

/**
 * Insert thousands separators into a numeric string.
 * @param {string} numStr - Number as a string, optionally with a '.' decimal.
 * @param {string} thousands - Thousands separator character.
 * @param {string} decimal - Decimal separator character.
 * @returns {string} The separated number string.
 */
function insertSeparators(numStr, thousands, decimal) {
  const parts = numStr.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
}

/**
 * Format a dollar amount using Shopify's money format template, with thousands
 * separators. Whole amounts can drop the trailing `.00` via `stripZeros`.
 * @param {number} amount - The amount in dollars.
 * @param {string} moneyFormat - The Shopify money format template.
 * @param {object} [options] - Formatting options.
 * @param {boolean} [options.stripZeros=false] - Drop `.00` for whole amounts.
 * @returns {string} The formatted price.
 */
function formatMoney(amount, moneyFormat, { stripZeros = false } = {}) {
  const val = Number(amount) || 0;
  const whole = stripZeros && Number.isInteger(val);
  const fixed = whole ? String(Math.round(val)) : val.toFixed(2);
  const noDecimals = String(Math.round(val));

  // Some markets' money_format templates embed HTML (e.g. a <span class=money> wrapper);
  // strip it so the result is safe to write via textContent without printing literal tags.
  const cleanFormat = typeof moneyFormat === 'string' ? moneyFormat.replace(/<[^>]*>/g, '') : moneyFormat;

  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = typeof cleanFormat === 'string' ? cleanFormat.match(placeholder) : null;
  const key = match ? match[1] : 'amount';

  let value;
  switch (key) {
    case 'amount_no_decimals':
      value = insertSeparators(noDecimals, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = insertSeparators(fixed, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = insertSeparators(noDecimals, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = insertSeparators(fixed, "'", '.');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = insertSeparators(noDecimals, ' ', '.');
      break;
    case 'amount_with_space_separator':
      value = insertSeparators(fixed, ' ', ',');
      break;
    case 'amount_with_period_and_space_separator':
      value = insertSeparators(fixed, ' ', '.');
      break;
    default:
      value = insertSeparators(fixed, ',', '.');
  }

  return match ? cleanFormat.replace(placeholder, value) : value;
}

/**
 * Format a cents amount using the money format template.
 * @param {number} cents - The amount in cents.
 * @param {string} moneyFormat - The Shopify money format template.
 * @param {object} [options] - Passed through to {@link formatMoney}.
 * @returns {string} The formatted price.
 */
function formatCents(cents, moneyFormat, options) {
  return formatMoney(cents / 100, moneyFormat, options);
}

/**
 * Add a number of business days (Mon–Fri) to a date.
 * @param {Date} startDate - The starting date.
 * @param {number} numDays - Business days to add.
 * @returns {Date} A new date `numDays` business days later.
 */
function addBusinessDays(startDate, numDays) {
  const date = new Date(startDate);
  let added = 0;
  while (added < numDays) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return date;
}

/**
 * Parse a business-day range out of a free-text rate description.
 * @param {string} description - The rate description.
 * @returns {{min: number, max: number}|null} The range, or null.
 */
function parseBusinessDays(description) {
  if (!description) return null;
  const rangeMatch = description.match(/(\d+)\s*(?:to|-)\s*(\d+)\s*business\s*days?/i);
  if (rangeMatch) return { min: parseInt(rangeMatch[1], 10), max: parseInt(rangeMatch[2], 10) };
  const singleMatch = description.match(/(\d+)\s*business\s*days?/i);
  if (singleMatch) {
    const days = parseInt(singleMatch[1], 10);
    return { min: days, max: days };
  }
  return null;
}

/**
 * Resolve a rate's delivery day range from its various possible fields.
 * @param {object} rate - A Shopify shipping rate.
 * @returns {{min: number, max: number}|{minDate: string, maxDate: string}|null} Business-day
 *   range, an ISO date range (carrier-calculated rates), or null.
 */
function extractDayRange(rate) {
  const range = rate.delivery_range;
  if (range && !Array.isArray(range) && range.min != null && range.max != null) {
    return { min: range.min, max: range.max };
  }
  if (Array.isArray(range) && range.length >= 2) {
    if (typeof range[0] === 'string') {
      return { minDate: range[0], maxDate: range[range.length - 1] };
    }
    return { min: range[0], max: range[range.length - 1] };
  }
  const days = rate.delivery_days;
  if (Array.isArray(days) && days.length > 0) {
    return { min: days[0], max: days[days.length - 1] };
  }
  if (typeof days === 'number') {
    return { min: days, max: days };
  }
  if (rate.description) {
    return parseBusinessDays(rate.description);
  }
  return null;
}

/**
 * Parse a 'YYYY-MM-DD' string as a local-midnight Date. Avoids
 * `new Date('YYYY-MM-DD')`, which parses as UTC midnight and so renders as
 * the previous day in negative-offset timezones.
 * @param {string} isoDate - Date in 'YYYY-MM-DD' form.
 * @returns {Date} The parsed date (Invalid Date if `isoDate` is malformed).
 */
function parseIsoDateLocal(isoDate) {
  const [year, month, day] = isoDate.split('-').map((part) => parseInt(part, 10));
  return new Date(year, month - 1, day);
}

/**
 * Format a delivery date range as localised "12 Aug" / "12 Aug – 15 Aug".
 * @param {{min: number, max: number}|{minDate: string, maxDate: string}} dayRange - Business-day
 *   range, or an explicit ISO date range from a carrier-calculated rate.
 * @param {string} locale - BCP-47 locale for date formatting.
 * @returns {string|null} The formatted date range, or null if the dates can't be parsed.
 */
function formatDateRange(dayRange, locale) {
  const formatter = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });

  if (dayRange.minDate != null && dayRange.maxDate != null) {
    const minDate = parseIsoDateLocal(dayRange.minDate);
    const maxDate = parseIsoDateLocal(dayRange.maxDate);
    if (Number.isNaN(minDate.getTime()) || Number.isNaN(maxDate.getTime())) return null;
    if (dayRange.minDate === dayRange.maxDate) return formatter.format(minDate);
    return `${formatter.format(minDate)} – ${formatter.format(maxDate)}`;
  }

  const today = new Date();
  const minDate = addBusinessDays(today, dayRange.min);
  if (dayRange.min === dayRange.max) return formatter.format(minDate);
  const maxDate = addBusinessDays(today, dayRange.max);
  return `${formatter.format(minDate)} – ${formatter.format(maxDate)}`;
}

/**
 * Await `ms` milliseconds, rejecting with an AbortError if `signal` aborts.
 * @param {number} ms - Delay in milliseconds.
 * @param {AbortSignal} [signal] - Optional abort signal.
 * @returns {Promise<void>} Resolves after the delay.
 */
function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      }, { once: true });
    }
  });
}

/* ---------------------------------------------------------------------------
 * Storage helpers (guarded against private-mode failures)
 * ------------------------------------------------------------------------- */

/**
 * Read a value from a Web Storage area, returning null on any failure.
 * @param {Storage} store - `localStorage` or `sessionStorage`.
 * @param {string} key - The storage key.
 * @returns {string|null} The stored value, or null.
 */
function safeGet(store, key) {
  try {
    return store.getItem(key);
  } catch (err) {
    return null;
  }
}

/**
 * Write a value to a Web Storage area, swallowing any failure.
 * @param {Storage} store - `localStorage` or `sessionStorage`.
 * @param {string} key - The storage key.
 * @param {string} value - The value to store.
 * @returns {void}
 */
function safeSet(store, key, value) {
  try {
    store.setItem(key, value);
  } catch (err) {
    /* storage unavailable — feature degrades to session-only */
  }
}

/**
 * Remove a value from a Web Storage area, swallowing any failure.
 * @param {Storage} store - `localStorage` or `sessionStorage`.
 * @param {string} key - The storage key.
 * @returns {void}
 */
function safeRemove(store, key) {
  try {
    store.removeItem(key);
  } catch (err) {
    /* storage unavailable */
  }
}

/* ---------------------------------------------------------------------------
 * Instance registry — one shipping estimate per page. Global document
 * listeners are bound once and dispatch to the current instance so theme-
 * editor section reloads never stack duplicate handlers.
 * ------------------------------------------------------------------------- */

let instance = null;
let globalListenersBound = false;

/**
 * Bind document-level listeners exactly once, delegating to the live instance.
 * @returns {void}
 */
function bindGlobalListeners() {
  if (globalListenersBound) return;
  globalListenersBound = true;

  const onCart = (event) => { if (instance) instance.handleCartUpdate(event); };
  document.addEventListener('cart:added', onCart);
  document.addEventListener('cart:updated', onCart);
  document.addEventListener('variant:changed', () => { if (instance) instance.handleVariantChange(); });
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (target && target.closest && target.closest('[data-js="add-to-cart-button"]') && instance) {
      instance.markAddToCartInFlight();
    }
  }, true);
}

/**
 * Uppercase a text input's value as the shopper types, without moving the
 * caret — used for markets whose postcodes contain letters (CA, GB).
 * @param {HTMLInputElement} input - The postcode input to bind.
 * @returns {void}
 */
function bindUppercaseInput(input) {
  input.addEventListener('input', () => {
    const { value } = input;
    if (value === value.toUpperCase()) return;
    const { selectionStart, selectionEnd } = input;
    input.value = value.toUpperCase();
    if (typeof input.setSelectionRange === 'function') {
      input.setSelectionRange(selectionStart, selectionEnd);
    }
  });
}

/**
 * Initialise (or re-initialise) the shipping estimate. Idempotent: an already
 * initialised container is skipped.
 * @returns {void}
 */
function initShippingEstimate() {
  const container = document.querySelector('[data-js="shipping-estimate"]:not([data-initialised="true"])');
  if (!container) return;

  const drawer = document.querySelector('.shipping-drawer-instance');
  const configEl = container.querySelector('[data-js="shipping-estimate-config"]');
  if (!drawer || !configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (err) {
    return;
  }

  container.dataset.initialised = 'true';

  const {
    countryCode = 'AU',
    moneyFormat = '{{amount}}',
    dateLocale = 'en-AU',
    standardThreshold = 0,
    expressThreshold = 0,
    standardCost = 0,
    expressCost = 0,
    fallbackVariantId = null,
    customerPostcode = null,
    variants = {},
    strings = {},
  } = config;

  const storageKey = `${LEGACY_STORAGE_KEY}:${countryCode}`;

  /* ----- element references (singletons) ----- */
  const qc = (sel) => container.querySelector(sel);
  const qd = (sel) => drawer.querySelector(sel);

  const postcodeInput = qc('[data-js="shipping-postcode-input"]');
  const checkBtn = qc('[data-js="shipping-check-btn"]');
  const errorEl = qc('[data-js="shipping-error"]');
  const persistentBar = qc('[data-js="shipping-persistent-bar"]');
  const localityLabel = qc('[data-js="shipping-locality"]');
  const barMessage = qc('[data-js="shipping-threshold-message"]');
  const barTrack = qc('.shipping-estimate__progress-track');
  const barProgressFill = qc('[data-js="shipping-progress-fill"]');
  const barMarkerStandard = qc('[data-js="shipping-marker-standard"]');

  const messageTemplates = {};
  container.querySelectorAll('template[data-message]').forEach((tpl) => {
    messageTemplates[tpl.dataset.message] = tpl;
  });

  const drawerLocationBlock = qd('[data-js="shipping-drawer-location"]');
  const drawerLocality = qd('[data-js="shipping-drawer-locality"]');
  const drawerInput = qd('[data-js="shipping-drawer-input"]');
  const drawerChangeBtn = qd('[data-js="shipping-drawer-change-btn"]');
  const drawerConfirmBtn = qd('[data-js="shipping-drawer-confirm-btn"]');
  const drawerError = qd('[data-js="shipping-drawer-error"]');
  const drawerMessage = qd('[data-js="shipping-drawer-message"]');
  const drawerProgress = qd('[data-js="shipping-drawer-progress"]');
  const drawerProgressFill = qd('[data-js="shipping-drawer-progress-fill"]');
  const drawerMarkerStandard = qd('[data-js="shipping-drawer-marker-standard"]');
  const drawerLegendStandard = qd('[data-js="shipping-drawer-legend-standard"]');
  const drawerLegendExpress = qd('[data-js="shipping-drawer-legend-express"]');
  const drawerResults = qd('[data-js="shipping-drawer-results"]');
  const rateWithDatesTpl = qd('[data-template="rate-with-dates"]');
  const rateFallbackTpl = qd('[data-template="rate-fallback"]');
  const rateSkeletonTpl = qd('[data-template="rate-skeleton"]');
  const noRatesTpl = qd('[data-template="no-rates"]');

  /* ----- per-instance runtime state ----- */
  let activeController = null;
  let currentRates = null;
  let lastCartTotal = 0;
  let lastCompleteStandard = false;
  let hasRenderedThreshold = false;
  let lastMessageSignature = null;
  let tempCartActive = false;
  let tempCartChain = Promise.resolve();
  let addToCartInFlight = false;
  let addToCartTimer = null;
  let totalSeq = 0;
  let pendingCartRefresh = false;
  let cartDiscountRate = 0;
  let ratesDeferred = false;
  let ratesStale = false;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Resolve the reduced-motion preference, preferring the shared themeUtils
   * surface over this module's own matchMedia fallback.
   * @returns {boolean} Whether motion should be suppressed.
   */
  function prefersReducedMotion() {
    if (window.themeUtils && typeof window.themeUtils.prefersReducedMotion === 'boolean') {
      return window.themeUtils.prefersReducedMotion;
    }
    return reduceMotion.matches;
  }

  /* ----- storage ----- */

  /**
   * Read the saved postcode for this market, migrating from the legacy
   * country-agnostic key on first run.
   * @returns {string|null} The saved postcode, or null.
   */
  function readSavedPostcode() {
    let postcode = safeGet(localStorage, storageKey);
    if (!postcode) {
      const legacy = safeGet(localStorage, LEGACY_STORAGE_KEY);
      if (legacy) {
        postcode = legacy;
        safeSet(localStorage, storageKey, legacy);
        safeRemove(localStorage, LEGACY_STORAGE_KEY);
      }
    }
    return postcode;
  }

  /**
   * Read cached, normalised rates for a postcode if still within TTL.
   * @param {string} postcode - The postcode key.
   * @returns {Array<object>|null} Cached rates, or null on miss/expiry.
   */
  function readCachedRates(postcode) {
    const raw = safeGet(sessionStorage, `${RATES_CACHE_PREFIX}${postcode}|${countryCode}`);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || (Date.now() - parsed.fetchedAt) > RATES_CACHE_TTL_MS) return null;
      return parsed.rates;
    } catch (err) {
      return null;
    }
  }

  /**
   * Cache normalised rates for a postcode with the current timestamp, and
   * persist a per-country snapshot in localStorage alongside it so the next
   * session can paint instantly before revalidating.
   * @param {string} postcode - The postcode key.
   * @param {Array<object>} rates - Normalised rates.
   * @returns {void}
   */
  function writeCachedRates(postcode, rates) {
    const fetchedAt = Date.now();
    safeSet(
      sessionStorage,
      `${RATES_CACHE_PREFIX}${postcode}|${countryCode}`,
      JSON.stringify({ rates, fetchedAt }),
    );
    safeSet(
      localStorage,
      `${RATES_SNAPSHOT_PREFIX}${countryCode}`,
      JSON.stringify({ postcode, fetchedAt, rates }),
    );
  }

  /**
   * Read the persisted per-country rates snapshot for instant paint across
   * sessions. Distinct from {@link readCachedRates}: this survives a fresh
   * session so a silent restore can show last-known rates before a
   * background revalidate confirms they are still current.
   * @param {string} postcode - The currently saved postcode to match.
   * @returns {{rates: Array<object>, stale: boolean}|null} The snapshot and
   *   whether it has outlived the session TTL, or null on miss, postcode
   *   mismatch, or expiry.
   */
  function readSnapshotRates(postcode) {
    const key = `${RATES_SNAPSHOT_PREFIX}${countryCode}`;
    const raw = safeGet(localStorage, key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed) return null;
      const age = Date.now() - parsed.fetchedAt;
      if (age > SNAPSHOT_MAX_AGE_MS) {
        safeRemove(localStorage, key);
        return null;
      }
      if (parsed.postcode !== postcode) return null;
      return { rates: parsed.rates, stale: age > RATES_CACHE_TTL_MS };
    } catch (err) {
      return null;
    }
  }

  /* ----- UI state setters ----- */

  /**
   * Set the container's resolved/input state.
   * @param {'input'|'resolved'} state - The target state.
   * @returns {void}
   */
  function setState(state) {
    container.dataset.state = state;
  }

  /**
   * Toggle the checking (loading) affordances on the input and drawer.
   * @param {boolean} on - Whether a check is in flight.
   * @returns {void}
   */
  function setChecking(on) {
    container.dataset.checking = on ? 'true' : 'false';
    drawer.dataset.checking = on ? 'true' : 'false';
    postcodeInput.disabled = on;
    checkBtn.disabled = on;
    if (drawerInput) drawerInput.disabled = on;
    if (drawerConfirmBtn) drawerConfirmBtn.disabled = on;
  }

  /**
   * Switch the drawer's location block between display and edit modes.
   * @param {'display'|'edit'} mode - The target mode.
   * @returns {void}
   */
  function setLocationMode(mode) {
    if (drawerLocationBlock) drawerLocationBlock.dataset.mode = mode;
  }

  /**
   * Show an error message inline (input state) or inside the drawer.
   * @param {string} message - The message to display.
   * @param {boolean} inDrawer - Whether to show it in the drawer.
   * @returns {void}
   */
  function showError(message, inDrawer) {
    if (inDrawer) {
      if (drawerError) drawerError.textContent = message;
    } else {
      errorEl.textContent = message;
    }
  }

  /**
   * Clear both error regions (auto-hidden via `:empty`).
   * @returns {void}
   */
  function clearErrors() {
    errorEl.textContent = '';
    if (drawerError) drawerError.textContent = '';
  }

  /**
   * Update the locality labels and input values for a postcode.
   * @param {string} postcode - The resolved postcode.
   * @returns {void}
   */
  function setLocalityUI(postcode) {
    const province = getProvinceFromPostcode(postcode, countryCode);
    const label = province ? `${province} ${postcode}` : `${strings.yourArea || ''} ${postcode}`;
    localityLabel.textContent = label;
    if (drawerLocality) drawerLocality.textContent = label;
    if (drawerInput) drawerInput.value = postcode;
    postcodeInput.value = postcode;
  }

  /**
   * Open the delivery drawer via the frozen drawer primitive API.
   * @returns {void}
   */
  function openDrawer() {
    if (window.BottomDrawer) window.BottomDrawer.open('shipping-drawer');
  }

  /* ----- motion helpers (all no-op under prefers-reduced-motion) ----- */

  /**
   * Animate a progress fill from 0 to its current width on the next frame.
   * @param {HTMLElement} fillEl - The progress fill element.
   * @returns {void}
   */
  function animateFill(fillEl) {
    if (!fillEl || prefersReducedMotion()) return;
    const target = fillEl.style.width;
    if (!target || target === '0%') return;
    fillEl.style.width = '0%';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { fillEl.style.width = target; });
    });
  }

  /**
   * Play a one-shot success pulse on a progress fill (threshold crossing).
   * @param {HTMLElement} fillEl - The progress fill element.
   * @returns {void}
   */
  function pulseFill(fillEl) {
    if (!fillEl || prefersReducedMotion()) return;
    fillEl.dataset.pulse = 'true';
    fillEl.addEventListener('animationend', () => { delete fillEl.dataset.pulse; }, { once: true });
  }

  /**
   * Play a one-shot fade on a threshold message after its content changes.
   * @param {HTMLElement} el - The message element.
   * @returns {void}
   */
  function fadeMessage(el) {
    if (!el || prefersReducedMotion()) return;
    el.dataset.updating = 'true';
    el.addEventListener('animationend', () => { delete el.dataset.updating; }, { once: true });
  }

  /**
   * Build a stable signature for a message spec, to detect real changes.
   * @param {object|null} spec - The message spec.
   * @returns {string} The signature.
   */
  function messageSignature(spec) {
    if (!spec) return '';
    const extra = spec.extra ? `${spec.extra.key}:${spec.extra.amount || ''}` : '';
    return `${spec.key}:${spec.amount || ''}|${extra}`;
  }

  /* ----- threshold messaging ----- */

  /**
   * Resolve which threshold message (and optional secondary line) applies.
   * Mirrors the legacy decision table exactly.
   * @param {number} cartTotalCents - Current cart total in cents.
   * @param {number} totalCents - Cart total plus the prospective variant price.
   * @returns {{key: string, amount?: string, extra?: object}|null} The spec.
   */
  function resolveMessageSpec(cartTotalCents, totalCents) {
    if (expressThreshold > 0 && cartTotalCents >= expressThreshold) {
      return { key: 'unlocked_express' };
    }
    if (standardThreshold > 0 && cartTotalCents >= standardThreshold) {
      const spec = { key: 'unlocked_standard' };
      if (expressThreshold > 0) {
        if (totalCents >= expressThreshold) {
          spec.extra = { key: 'add_and_unlock_express' };
        } else {
          spec.extra = { key: 'away_from_express', amount: formatCents(expressThreshold - totalCents, moneyFormat) };
        }
      }
      return spec;
    }
    if (expressThreshold > 0 && totalCents >= expressThreshold) {
      return { key: 'add_and_unlock_express' };
    }
    if (standardThreshold > 0 && totalCents >= standardThreshold) {
      return { key: 'add_to_unlock_both' };
    }
    if (standardThreshold > 0) {
      return { key: 'add_to_unlock_standard', amount: formatCents(standardThreshold - totalCents, moneyFormat) };
    }
    if (expressThreshold > 0) {
      return { key: 'add_to_unlock_express', amount: formatCents(expressThreshold - totalCents, moneyFormat) };
    }
    return null;
  }

  /**
   * Clone a message template, filling its amount slot if present.
   * @param {string} key - The `data-message` key.
   * @param {string} [amount] - Formatted amount for the slot.
   * @returns {DocumentFragment|null} The composed fragment.
   */
  function buildMessageFragment(key, amount) {
    const tpl = messageTemplates[key];
    if (!tpl) return null;
    const fragment = tpl.content.cloneNode(true);
    if (amount != null) {
      const slot = fragment.querySelector('[data-slot="amount"]');
      if (slot) slot.textContent = amount;
    }
    return fragment;
  }

  /**
   * Render a resolved message spec into a target element using cloned
   * template fragments and textContent only (no innerHTML).
   * @param {HTMLElement} el - Target element (bar or drawer message).
   * @param {object|null} spec - The message spec from {@link resolveMessageSpec}.
   * @returns {void}
   */
  function renderMessageInto(el, spec) {
    if (!el) return;
    while (el.firstChild) el.firstChild.remove();
    if (!spec) return;

    const primary = buildMessageFragment(spec.key, spec.amount);
    if (primary) el.appendChild(primary);

    if (spec.extra) {
      const extra = buildMessageFragment(spec.extra.key, spec.extra.amount);
      if (extra) {
        el.appendChild(document.createTextNode(' '));
        el.appendChild(document.createElement('br'));
        const span = document.createElement('span');
        span.className = 'shipping-estimate__extra-message';
        span.appendChild(extra);
        el.appendChild(span);
      }
    }
  }

  /**
   * Update both progress bars, markers, legends and threshold messages.
   * @param {number} cartTotalCents - Current cart total in cents.
   * @param {number} variantPriceCents - Prospective variant price in cents.
   * @returns {void}
   */
  function renderThreshold(cartTotalCents, variantPriceCents) {
    lastCartTotal = cartTotalCents;
    // Prospective total scales the variant price by the cart's blended
    // discount rate, so a sitewide code already in the cart doesn't promise
    // a tier the discounted add can't reach (exact for percentage codes;
    // unchanged when no discount is applied).
    const totalCents = cartTotalCents + Math.round(variantPriceCents * (1 - cartDiscountRate));

    const hasBoth = expressThreshold > 0 && standardThreshold > 0 && expressThreshold > standardThreshold;
    const maxThreshold = Math.max(standardThreshold, expressThreshold);

    let percent = maxThreshold > 0 ? (cartTotalCents / maxThreshold) * 100 : 0;
    if (percent > 100) percent = 100;
    barProgressFill.style.width = `${percent}%`;
    if (drawerProgressFill) drawerProgressFill.style.width = `${percent}%`;

    const standardStr = formatCents(standardThreshold, moneyFormat, { stripZeros: true });
    const expressStr = formatCents(expressThreshold, moneyFormat, { stripZeros: true });

    if (hasBoth) {
      const standardPercent = (standardThreshold / expressThreshold) * 100;
      barMarkerStandard.style.left = `${standardPercent}%`;
      if (drawerMarkerStandard) drawerMarkerStandard.style.left = `${standardPercent}%`;
      if (drawerLegendStandard) {
        drawerLegendStandard.textContent = `${standardStr} ${strings.standard}`;
        drawerLegendStandard.style.left = `${standardPercent}%`;
      }
      if (drawerLegendExpress) drawerLegendExpress.textContent = `${expressStr} ${strings.express}`;
      barTrack.dataset.tiers = 'both';
      if (drawerProgress) drawerProgress.dataset.tiers = 'both';
    } else if (maxThreshold > 0) {
      if (drawerLegendExpress) {
        drawerLegendExpress.textContent = standardThreshold > 0
          ? `${standardStr} ${strings.standard}`
          : `${expressStr} ${strings.express}`;
      }
      barTrack.dataset.tiers = 'single';
      if (drawerProgress) drawerProgress.dataset.tiers = 'single';
    } else {
      barTrack.dataset.tiers = 'none';
      if (drawerProgress) drawerProgress.dataset.tiers = 'none';
    }

    const complete = (standardThreshold > 0 && cartTotalCents >= standardThreshold)
      || (standardThreshold === 0 && expressThreshold > 0 && cartTotalCents >= expressThreshold);
    barProgressFill.dataset.complete = complete ? 'true' : 'false';
    if (drawerProgressFill) drawerProgressFill.dataset.complete = complete ? 'true' : 'false';

    // One-shot pulse when the standard threshold is crossed (not on first render).
    if (complete && !lastCompleteStandard && hasRenderedThreshold) {
      pulseFill(barProgressFill);
      pulseFill(drawerProgressFill);
    }

    const spec = resolveMessageSpec(cartTotalCents, totalCents);
    const signature = messageSignature(spec);
    renderMessageInto(barMessage, spec);
    renderMessageInto(drawerMessage, spec);
    if (hasRenderedThreshold && signature !== lastMessageSignature) {
      fadeMessage(barMessage);
      fadeMessage(drawerMessage);
    }

    lastMessageSignature = signature;
    lastCompleteStandard = complete;
    hasRenderedThreshold = true;
  }

  /* ----- rate rows ----- */

  /**
   * Normalise a raw Shopify rate for caching (name, price, day range, desc).
   * @param {object} rate - A raw shipping rate.
   * @returns {object} The normalised rate.
   */
  function normaliseRate(rate) {
    return {
      name: rate.name,
      price: rate.price,
      dayRange: extractDayRange(rate),
      description: rate.description || '',
    };
  }

  /**
   * Apply FREE / strike-through pricing to a rate row's price cells.
   * @param {HTMLElement} priceEl - The current-price element.
   * @param {HTMLElement} originalEl - The strike-through original-price element.
   * @param {object} rate - The normalised rate.
   * @param {number} cartTotalCents - Current cart total in cents.
   * @returns {void}
   */
  function applyRatePrice(priceEl, originalEl, rate, cartTotalCents) {
    const name = (rate.name || '').toLowerCase();
    const isStandard = name.includes('standard');
    const isExpress = name.includes('express');

    let displayPrice = parseFloat(rate.price);
    if (displayPrice === 0) {
      if (isStandard && standardCost > 0) displayPrice = standardCost / 100;
      if (isExpress && expressCost > 0) displayPrice = expressCost / 100;
    }

    let makeFree = false;
    if (isExpress && expressThreshold > 0 && cartTotalCents >= expressThreshold) makeFree = true;
    if (isStandard && standardThreshold > 0 && cartTotalCents >= standardThreshold) makeFree = true;

    if (parseFloat(rate.price) === 0) {
      const isUnderStandardThreshold = isStandard && standardThreshold > 0 && cartTotalCents < standardThreshold;
      const isUnderExpressThreshold = isExpress && expressThreshold > 0 && cartTotalCents < expressThreshold;
      if (!isUnderStandardThreshold && !isUnderExpressThreshold) {
        makeFree = true;
      }
    }

    if (makeFree) {
      if (originalEl) {
        if (displayPrice > 0) {
          originalEl.textContent = formatMoney(displayPrice, moneyFormat);
          originalEl.hidden = false;
        } else {
          originalEl.textContent = '';
          originalEl.hidden = true;
        }
      }
      priceEl.textContent = strings.free;
      priceEl.dataset.free = 'true';
    } else {
      if (originalEl) {
        originalEl.textContent = '';
        originalEl.hidden = true;
      }
      priceEl.textContent = formatMoney(displayPrice, moneyFormat);
      delete priceEl.dataset.free;
    }
  }

  /**
   * Render skeleton placeholder rows while rates load.
   * @param {number} [count=2] - Number of skeleton rows.
   * @returns {void}
   */
  function renderSkeletons(count = 2) {
    while (drawerResults.firstChild) drawerResults.firstChild.remove();
    if (!rateSkeletonTpl) return;
    for (let i = 0; i < count; i += 1) {
      drawerResults.appendChild(rateSkeletonTpl.content.cloneNode(true));
    }
  }

  /**
   * Build the rate rows from normalised rates.
   * @param {Array<object>} rates - Normalised rates.
   * @param {number} cartTotalCents - Current cart total in cents.
   * @returns {void}
   */
  function renderRates(rates, cartTotalCents) {
    currentRates = rates;
    while (drawerResults.firstChild) drawerResults.firstChild.remove();

    if (!rates || rates.length === 0) {
      if (noRatesTpl) drawerResults.appendChild(noRatesTpl.content.cloneNode(true));
      return;
    }

    rates.forEach((rate, index) => {
      const dateRange = rate.dayRange ? formatDateRange(rate.dayRange, dateLocale) : null;
      const tpl = dateRange ? rateWithDatesTpl : rateFallbackTpl;
      if (!tpl) return;

      const clone = tpl.content.cloneNode(true);
      const row = clone.querySelector('.shipping-drawer__tier');
      if (row) row.style.setProperty('--item-index', index);

      clone.querySelector('[data-rate-name]').textContent = rate.name;
      if (dateRange) {
        clone.querySelector('[data-rate-dates]').textContent = dateRange;
      } else {
        const descEl = clone.querySelector('[data-rate-description]');
        if (descEl) descEl.textContent = rate.description || '';
      }

      applyRatePrice(
        clone.querySelector('[data-rate-price]'),
        clone.querySelector('[data-rate-price-original]'),
        rate,
        cartTotalCents,
      );

      drawerResults.appendChild(clone);
    });
  }

  /**
   * Update FREE pricing on already-rendered rows in place (no rebuild), so
   * cart/variant changes don't replay the row entrance animation.
   * @param {number} cartTotalCents - Current cart total in cents.
   * @returns {void}
   */
  function updateRateRows(cartTotalCents) {
    if (!currentRates || currentRates.length === 0) return;
    const rows = drawerResults.querySelectorAll('.shipping-drawer__tier');
    currentRates.forEach((rate, i) => {
      const row = rows[i];
      if (!row) return;
      applyRatePrice(
        row.querySelector('[data-rate-price]'),
        row.querySelector('[data-rate-price-original]'),
        rate,
        cartTotalCents,
      );
    });
  }

  /* ----- networking ----- */

  /**
   * Resolve the currently selected variant (or the fallback) and its price.
   * @returns {{id: string, price: number}} The variant id and price in cents.
   */
  function getSelectedVariant() {
    const input = document.querySelector('[data-js="variant-id-input"]') || document.querySelector('input[name="id"]');
    const id = input && input.value ? String(input.value).trim() : '';
    if (id && variants[id] != null) return { id, price: variants[id] };
    const fid = fallbackVariantId != null ? String(fallbackVariantId) : '';
    return { id: fid, price: fid && variants[fid] != null ? variants[fid] : 0 };
  }

  /**
   * Fetch the current cart.
   * @param {AbortSignal} [signal] - Optional abort signal.
   * @returns {Promise<object>} The cart object.
   */
  async function fetchCart(signal) {
    const res = await fetch(`${window.Shopify.routes.root}cart.js`, { signal });
    return res.json();
  }

  /**
   * Derive the cart's blended discount rate (0–1) from a cart payload, so
   * prospective prices can anticipate a sitewide code already in the cart.
   * @param {object} cart - A cart JSON payload.
   * @returns {number} The discount fraction, 0 when none is observable.
   */
  function discountRateFrom(cart) {
    const total = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
    const discount = cart && typeof cart.total_discount === 'number' ? cart.total_discount : 0;
    const original = total + discount;
    return original > 0 ? discount / original : 0;
  }

  /**
   * Fetch the cart total with latest-wins ordering: if a newer total is
   * applied while this fetch is in flight, the stale response is discarded.
   * @param {AbortSignal} [signal] - Optional abort signal.
   * @returns {Promise<number|null>} The total in cents, or null when the
   *   response was superseded or the fetch failed.
   */
  async function fetchLatestCartTotal(signal) {
    const seq = ++totalSeq;
    try {
      const cart = await fetchCart(signal);
      if (seq !== totalSeq) return null;
      cartDiscountRate = discountRateFrom(cart);
      return cart.total_price || 0;
    } catch (err) {
      return null;
    }
  }

  /**
   * Fetch live shipping rates for a postcode via prepare + poll, with an
   * abortable, backed-off poll cadence.
   * @param {string} postcode - The postcode.
   * @param {AbortSignal} signal - Abort signal for the whole operation.
   * @returns {Promise<Array<object>>} Raw shipping rates.
   */
  async function fetchShippingRates(postcode, signal) {
    const province = getProvinceFromPostcode(postcode, countryCode);
    const params = new URLSearchParams({
      'shipping_address[zip]': postcode,
      'shipping_address[country]': countryCode,
      'shipping_address[province]': province,
    });

    const prepareRes = await fetch(`${window.Shopify.routes.root}cart/prepare_shipping_rates.json?${params.toString()}`, { method: 'POST', signal });
    if (!prepareRes.ok && prepareRes.status === 422) {
      let errorJson = null;
      try {
        errorJson = await prepareRes.json();
      } catch (err) {
        errorJson = null;
      }
      if (errorJson) {
        const messages = [];
        if (errorJson.zip) messages.push(...errorJson.zip);
        if (errorJson.province) messages.push(...errorJson.province);
        if (errorJson.country) messages.push(...errorJson.country);
        if (messages.length > 0) throw new Error(`VALIDATION_ERROR: ${messages.join('. ')}`);
      }
      throw new Error('EMPTY_CART');
    }
    if (!prepareRes.ok) throw new Error('FETCH_FAILED');

    let attempt = 0;
    let waited = 0;
    while (waited < POLL_MAX_MS) {
      const wait = attempt === 0 ? 150 : Math.min(400 + (attempt * 100), 1500);
      await delay(wait, signal);
      waited += wait;
      const asyncRes = await fetch(`${window.Shopify.routes.root}cart/async_shipping_rates.json?${params.toString()}`, { signal });
      if (asyncRes.status === 422) throw new Error('EMPTY_CART');
      if (!asyncRes.ok) throw new Error('FETCH_FAILED');
      const data = await asyncRes.json();
      if (data === null) {
        attempt += 1;
        continue;
      }
      return data.shipping_rates || [];
    }
    throw new Error('FETCH_FAILED');
  }

  /**
   * Fetch rates for an empty cart by temporarily adding the variant, then
   * restoring it from a fresh cart read afterwards, so a genuine add for the
   * same variant during the window is preserved rather than overwritten.
   * Hardened: restores on abort and on page-hide, retries the restore once,
   * re-syncs the minicart via `cart:added` when the retry still fails or a
   * cart event landed mid-window, and never runs while a real add-to-cart is
   * in flight.
   * @param {string} variantId - The variant to add temporarily.
   * @param {string} postcode - The postcode.
   * @param {AbortSignal} signal - Abort signal for the operation.
   * @returns {Promise<Array<object>>} Raw shipping rates.
   */
  function fetchRatesWithTempCart(variantId, postcode, signal) {
    if (addToCartInFlight) return Promise.reject(new Error('EMPTY_CART'));

    // Serialise temp-cart mutations: a superseded check's restore always
    // settles before the next add reads the cart, so no phantom item can
    // survive from an interleaved add/restore.
    const run = tempCartChain.then(() => tempCartCore(variantId, postcode, signal));
    tempCartChain = run.catch(() => {});
    return run;
  }

  /**
   * The add → fetch → restore body of the temp-cart path (serialised by
   * {@link fetchRatesWithTempCart}).
   * @param {string} variantId - The variant to add temporarily.
   * @param {string} postcode - The postcode.
   * @param {AbortSignal} signal - Abort signal for the operation.
   * @returns {Promise<Array<object>>} Raw shipping rates.
   */
  async function tempCartCore(variantId, postcode, signal) {
    const originalCart = await fetchCart(signal);
    const existing = originalCart.items.find((item) => String(item.variant_id) === String(variantId));
    const previousQuantity = existing ? existing.quantity : 0;
    // Keepalive-only fallback: pagehide cannot await a fresh cart read, so it
    // replays this pre-add snapshot instead of the live-computed target below.
    const keepaliveRestoreBody = JSON.stringify({ updates: { [variantId]: previousQuantity } });

    let restored = false;
    let addRejected = false;

    /**
     * Restore the cart to its pre-check state. The keepalive (page-hide)
     * call has no time to read the cart, so it replays the pre-add snapshot;
     * every other call re-reads the live cart and backs off by the 1 unit
     * this run itself added, so a genuine add for the same variant landing
     * mid-window is never clobbered by a stale target. A definitively
     * rejected add skips the restore outright — nothing was added. Throws
     * `RESTORE_FAILED` if the attempt and its retry both fail.
     * @param {boolean} keepalive - True only from the pagehide handler.
     * @returns {Promise<void>} Resolves once the restore attempt settles.
     */
    const restore = async (keepalive) => {
      if (restored) return;
      restored = true;

      const attemptRestore = async () => {
        // A rejected add contributed nothing to the cart, so any restore —
        // live-computed or keepalive — would subtract a unit from a genuine
        // add that raced into the same window.
        if (addRejected) return;
        if (keepalive) {
          await fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: keepaliveRestoreBody,
            keepalive: true,
          });
          return;
        }
        const liveCart = await fetchCart();
        const liveItem = liveCart.items.find((item) => String(item.variant_id) === String(variantId));
        const liveQuantity = liveItem ? liveItem.quantity : 0;
        if (liveQuantity === 0) return;
        await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: { [variantId]: Math.max(0, liveQuantity - 1) } }),
        });
      };

      try {
        await attemptRestore();
      } catch (err) {
        try {
          await attemptRestore();
        } catch (retryErr) {
          throw new Error('RESTORE_FAILED');
        }
      }
    };

    const onPageHide = () => { restore(true); };
    window.addEventListener('pagehide', onPageHide);
    tempCartActive = true;

    try {
      const addRes = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
        signal,
      });
      if (!addRes.ok) {
        addRejected = true;
        throw new Error('FETCH_FAILED');
      }
      return await fetchShippingRates(postcode, signal);
    } finally {
      window.removeEventListener('pagehide', onPageHide);
      try {
        await restore(false);
        // The flag must flip before the dispatch: handleCartUpdate reads it
        // to decide between deferring and re-rendering, and this module's
        // own listener must take the re-rendering branch for this event.
        tempCartActive = false;
        if (pendingCartRefresh) {
          document.dispatchEvent(new CustomEvent('cart:added', { detail: { openMinicart: false } }));
        }
      } catch (err) {
        tempCartActive = false;
        // The retry also failed: the cart is left holding a unit nobody
        // asked for, with no recovery left but to surface it so the
        // minicart shows the shopper what is really in their cart.
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { openMinicart: false } }));
        throw err;
      }
    }
  }

  /**
   * Acquire rates for a postcode: cache first, then network. Empty carts use
   * the hardened temp-cart path. Normalised rates are cached on success.
   * @param {string} postcode - The postcode.
   * @param {object} cart - The current cart.
   * @param {{id: string, price: number}} variant - The selected variant.
   * @param {AbortSignal} signal - Abort signal.
   * @returns {Promise<Array<object>>} Normalised rates.
   */
  async function acquireRates(postcode, cart, variant, signal) {
    const cached = readCachedRates(postcode);
    if (cached) return cached;

    let rawRates;
    if (cart.items && cart.items.length > 0) {
      rawRates = await fetchShippingRates(postcode, signal);
    } else {
      if (!variant.id) throw new Error('EMPTY_CART_NO_VARIANT');
      rawRates = await fetchRatesWithTempCart(variant.id, postcode, signal);
    }

    const normalised = rawRates.map(normaliseRate);
    writeCachedRates(postcode, normalised);
    return normalised;
  }

  /* ----- orchestration ----- */

  /**
   * Run a full postcode check: validate, fetch cart + rates (from cache or
   * network), render the resolved bar and drawer.
   * @param {string} rawPostcode - The postcode to check.
   * @param {object} [options] - Options.
   * @param {boolean} [options.openDrawerAfter=true] - Open + show loading UI.
   * @returns {Promise<void>} Resolves when the check settles.
   */
  async function runCheck(rawPostcode, { openDrawerAfter = true } = {}) {
    const postcode = normalisePostcode(rawPostcode, countryCode);

    if (!postcode) {
      safeRemove(localStorage, storageKey);
      currentRates = null;
      setState('input');
      return;
    }

    if (!isValidPostcode(postcode, countryCode)) {
      showError(strings.invalidPostcode || strings.error || '', openDrawerAfter);
      if (openDrawerAfter) {
        setLocationMode('edit');
        openDrawer();
      }
      return;
    }

    if (activeController) activeController.abort();
    const controller = new AbortController();
    activeController = controller;
    const { signal } = controller;

    clearErrors();
    setChecking(true);
    setLocationMode('display');
    setLocalityUI(postcode);
    safeSet(localStorage, storageKey, postcode);

    if (openDrawerAfter) {
      // Show skeletons only when nothing is rendered yet; a re-check keeps the
      // existing rows visible while the fresh rates load.
      if (!currentRates || currentRates.length === 0) renderSkeletons();
      openDrawer();
    }

    try {
      const variant = getSelectedVariant();
      // Wait for any superseded temp-cart mutation to fully restore before
      // reading the cart, so the threshold total never reflects a transient
      // temp item from an aborted check.
      await tempCartChain.catch(() => {});
      if (signal.aborted) return;
      const cart = await fetchCart(signal);
      totalSeq += 1;
      cartDiscountRate = discountRateFrom(cart);
      renderThreshold(cart.total_price || 0, variant.price);

      if (!openDrawerAfter) {
        // Silent page-load restores never run the temp-cart mutation churn:
        // rates only ever render inside the drawer, so acquisition can wait
        // for the shopper to actually open it (see the drawer:open listener).
        const cached = readCachedRates(postcode);
        const snapshot = cached ? null : readSnapshotRates(postcode);
        if (cached) {
          renderRates(cached, lastCartTotal);
          setState('resolved');
          animateFill(barProgressFill);
        } else if (snapshot) {
          renderRates(snapshot.rates, lastCartTotal);
          if (snapshot.stale) ratesStale = true;
          setState('resolved');
          animateFill(barProgressFill);
        } else {
          ratesDeferred = true;
          setState('resolved');
          animateFill(barProgressFill);
        }
        return;
      }

      const rates = await acquireRates(postcode, cart, variant, signal);

      // The rates poll can take seconds; if a cart event was suppressed while
      // the temp-cart item was live, re-read the total so the rows and bar
      // reflect the cart as it is now.
      if (pendingCartRefresh) {
        pendingCartRefresh = false;
        const freshTotal = await fetchLatestCartTotal(signal);
        if (freshTotal !== null) renderThreshold(freshTotal, getSelectedVariant().price);
      }
      if (signal.aborted) return;
      renderRates(rates, lastCartTotal);

      setState('resolved');
      animateFill(barProgressFill);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      handleError(err, openDrawerAfter);
    } finally {
      // Only the live check may clear the loading state — a superseded check
      // settling late must not wipe the newer check's spinner.
      if (activeController === controller) {
        activeController = null;
        setChecking(false);
      }
    }
  }

  /**
   * Map a thrown error to the appropriate user-facing message and UI.
   * @param {Error} err - The error thrown during a check.
   * @param {boolean} inDrawer - Whether the drawer is the active surface.
   * @returns {void}
   */
  function handleError(err, inDrawer) {
    // Clear any skeleton placeholders left over from the loading state.
    drawerResults.querySelectorAll('.shipping-drawer__tier[data-skeleton]').forEach((el) => el.remove());
    const message = (err && err.message) || '';
    if (message.startsWith('VALIDATION_ERROR: ')) {
      showError(message.replace('VALIDATION_ERROR: ', ''), inDrawer);
    } else if (message === 'EMPTY_CART_NO_VARIANT') {
      showError(strings.errorEmptyCart || strings.error || '', inDrawer);
    } else {
      showError(strings.error || '', inDrawer);
    }
    if (inDrawer) setLocationMode('edit');
  }

  /**
   * Re-render the resolved bar/drawer locally from cached rates using a fresh
   * cart total — no rate network traffic. Used for cart and variant events.
   * @param {object|null} cartData - Cart payload from an event, if available.
   * @returns {Promise<void>} Resolves when the re-render completes.
   */
  async function rerenderFromCache(cartData) {
    const postcode = readSavedPostcode();
    if (!postcode) return;

    let cartTotal;
    if (cartData && typeof cartData.total_price === 'number') {
      totalSeq += 1;
      cartDiscountRate = discountRateFrom(cartData);
      cartTotal = cartData.total_price;
    } else {
      cartTotal = await fetchLatestCartTotal();
      if (cartTotal === null) return;
    }

    const variant = getSelectedVariant();
    renderThreshold(cartTotal, variant.price);
    updateRateRows(cartTotal);
  }

  /**
   * Revalidate a stale snapshot in the background once the drawer is open.
   * Leaves the snapshot rows on screen untouched while it runs (no spinner)
   * and only re-renders them if the fresh rates actually differ, so an
   * unchanged result doesn't replay the row entrance animation.
   * @returns {Promise<void>} Resolves when the revalidation settles.
   */
  async function revalidateSnapshot() {
    const postcode = readSavedPostcode();
    if (!postcode) return;

    const controller = new AbortController();
    activeController = controller;
    const { signal } = controller;

    try {
      const variant = getSelectedVariant();
      const cart = await fetchCart(signal);
      totalSeq += 1;
      cartDiscountRate = discountRateFrom(cart);

      const freshRates = await acquireRates(postcode, cart, variant, signal);
      if (signal.aborted) return;

      // Threshold and rows update together once the fresh data is in hand,
      // rather than the bar jumping ahead of the rows it's still waiting on.
      renderThreshold(cart.total_price || 0, variant.price);
      if (JSON.stringify(freshRates) !== JSON.stringify(currentRates)) {
        renderRates(freshRates, lastCartTotal);
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      // Snapshot rows already on screen stay put — a background revalidate
      // the shopper never directly asked for shows no error UI on failure.
    } finally {
      if (activeController === controller) activeController = null;
    }
  }

  /* ----- public instance surface (called by global listeners) ----- */

  /**
   * Handle a cart lifecycle event by re-rendering locally from cache.
   * @param {CustomEvent} event - The cart event.
   * @returns {void}
   */
  function handleCartUpdate(event) {
    addToCartInFlight = false;
    if (addToCartTimer) {
      clearTimeout(addToCartTimer);
      addToCartTimer = null;
    }
    if (tempCartActive) {
      // The cart currently holds the transient temp-cart item, so any total
      // read now would be wrong — note the miss and re-sync after restore.
      pendingCartRefresh = true;
      return;
    }
    const cartData = event && event.detail ? event.detail.cartData : null;
    rerenderFromCache(cartData || null);
  }

  /**
   * Handle a variant change by re-running the threshold maths with the new
   * prospective price. Pure local re-render, no network.
   * @returns {void}
   */
  function handleVariantChange() {
    if (container.dataset.state !== 'resolved') return;
    const variant = getSelectedVariant();
    renderThreshold(lastCartTotal, variant.price);
    updateRateRows(lastCartTotal);
  }

  /**
   * Note that a real PDP add-to-cart is in flight, so the temp-cart path
   * stands down to avoid racing (and corrupting) the shopper's cart.
   * @returns {void}
   */
  function markAddToCartInFlight() {
    addToCartInFlight = true;
    if (addToCartTimer) clearTimeout(addToCartTimer);
    addToCartTimer = setTimeout(() => { addToCartInFlight = false; }, 5000);
  }

  instance = { handleCartUpdate, handleVariantChange, markAddToCartInFlight };

  /* ----- local (per-instance) event wiring ----- */

  checkBtn.addEventListener('click', () => runCheck(postcodeInput.value, { openDrawerAfter: true }));
  postcodeInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      runCheck(postcodeInput.value, { openDrawerAfter: true });
    }
  });
  persistentBar.addEventListener('click', openDrawer);

  if (drawerConfirmBtn) {
    drawerConfirmBtn.addEventListener('click', () => runCheck(drawerInput.value, { openDrawerAfter: true }));
  }
  if (drawerInput) {
    drawerInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        runCheck(drawerInput.value, { openDrawerAfter: true });
      }
    });
  }
  if (drawerChangeBtn) {
    drawerChangeBtn.addEventListener('click', () => {
      setLocationMode('edit');
      if (drawerInput) drawerInput.focus();
    });
  }

  if (countryCode === 'CA' || countryCode === 'GB') {
    bindUppercaseInput(postcodeInput);
    if (drawerInput) bindUppercaseInput(drawerInput);
  }

  // Animate the drawer progress fill from 0 → value each time the drawer opens.
  drawer.addEventListener('drawer:opened', () => animateFill(drawerProgressFill));

  // Complete a deferred silent restore, or revalidate a stale snapshot in the
  // background, the moment the shopper opens the drawer. `drawer:open` fires
  // as opening starts (earlier than `drawer:opened` above), so a deferred
  // fetch begins before the opening transition even finishes.
  drawer.addEventListener('drawer:open', () => {
    if (activeController) return;
    if (ratesDeferred) {
      const postcode = readSavedPostcode();
      if (!postcode) return;
      ratesDeferred = false;
      runCheck(postcode, { openDrawerAfter: true });
    } else if (ratesStale && currentRates) {
      ratesStale = false;
      revalidateSnapshot();
    }
  });

  bindGlobalListeners();

  // Restore a saved postcode silently (no drawer) on load.
  const saved = readSavedPostcode();
  if (saved) {
    runCheck(saved, { openDrawerAfter: false });
  } else if (customerPostcode) {
    // Seeds a signed-in shopper's estimate from their default address so
    // they don't have to retype it; validated first since admin-entered
    // addresses aren't guaranteed to match the market's postcode format.
    // The silent runCheck path is cheap — it only renders the threshold bar
    // and defers rate fetching until the drawer opens.
    const candidate = normalisePostcode(customerPostcode, countryCode);
    if (isValidPostcode(candidate, countryCode)) runCheck(candidate, { openDrawerAfter: false });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShippingEstimate);
} else {
  initShippingEstimate();
}

document.addEventListener('shopify:section:load', initShippingEstimate);
